import { useQuery } from '@tanstack/react-query';

import { AdminStatisticsMessage } from '../../../types/StatisticsMessage';
import {
    getCustomerReports,
    getPortReports,
    getProductReports,
    getSupplierReports,
    getVesselReports,
} from '../../common/api/clients/gateway.api';
import { getGeneralReport, getProductGroupReport } from '../../common/api/clients/report.api';
import { queries } from '../../common/api/queryKeys/queries';
import { mapProductGroupsReports } from '../utils/mapProductGroupsReports';
import { sortReportsByName } from '../utils/sortReports';

interface FetchAdminStatisticsParams {
    from: number;
    to: number;
    customerIds?: string[];
    supplierIds?: string[];
}

export const useAdminStatistics = (params: FetchAdminStatisticsParams) => {
    const fetchAdminStatistics = async (params: FetchAdminStatisticsParams): Promise<AdminStatisticsMessage> => {
        const [general, vessel, supplier, customer, product, port, productGroup] = await Promise.all([
            getGeneralReport(params),
            getVesselReports(params),
            getSupplierReports(params),
            getCustomerReports(params),
            getProductReports(params),
            getPortReports(params),
            getProductGroupReport(params),
        ]);

        const mappedVesselReports = Object.values(vessel.reports);
        const mappedSupplierReports = Object.values(supplier.reports);
        const mappedCustomerReports = Object.values(customer.reports);
        const mappedPortReports = Object.values(port.reports);
        const mappedProductReports = Object.values(product.reports);

        const mappedAndSortedProductGroupReports = mapProductGroupsReports(productGroup.reports).sort((a, b) =>
            a.productGroup.localeCompare(b.productGroup)
        );

        return {
            period: general.period,
            general: general.item,
            vessel: mappedVesselReports.sort(sortReportsByName),
            supplier: mappedSupplierReports.sort(sortReportsByName),
            customer: mappedCustomerReports.sort(sortReportsByName),
            port: mappedPortReports.sort(sortReportsByName),
            product: mappedProductReports.sort(sortReportsByName),
            productGroup: mappedAndSortedProductGroupReports,
        };
    };

    const fiveMinutesInMilliseconds = 5 * 60 * 1000;

    return useQuery({
        queryKey: queries.reports.admin(params).queryKey,
        queryFn: () => fetchAdminStatistics(params),
        staleTime: fiveMinutesInMilliseconds,
    });
};
